import { EncryptionService } from '../zk/encryption.service';
import { decryptInBatches } from '../zk/batch-decrypt.util';
import { Customer, CustomerWithEncryptedPii } from './customer.model';

// Shared by CustomersEffects (full list/detail loads) and ClientSearchService
// (Phase 6 candidate-set search) so both decrypt a Customer record identically.
export async function decryptCustomerRecord(
  encryption: EncryptionService,
  record: CustomerWithEncryptedPii,
): Promise<Customer> {
  if (!record.wrappedDek || Object.keys(record.encryptedFields).length === 0) {
    return toPlainCustomer(record, {}, {});
  }
  try {
    // Combine customer fields + address fields (prefixed) into one decrypt call —
    // addresses share the customer's DEK, so this is a single RSA unwrap.
    const combined: Record<string, string> = { ...record.encryptedFields };
    for (const addr of record.addresses) {
      for (const [k, v] of Object.entries(addr.encryptedFields)) {
        combined[`__addr${addr.id}_${k}`] = v;
      }
    }
    const plain = await encryption.decryptRecord(combined, record.wrappedDek, `Customer:${record.id}`);
    const addrPlains: Record<number, Record<string, string>> = {};
    for (const addr of record.addresses) {
      addrPlains[addr.id] = {};
      for (const k of Object.keys(addr.encryptedFields)) {
        const pkey = `__addr${addr.id}_${k}`;
        if (plain[pkey]) addrPlains[addr.id][k] = plain[pkey];
      }
    }
    return toPlainCustomer(record, plain, addrPlains);
  } catch {
    // Decryption failed (e.g. not enrolled) — show raw sentinel values
    return toPlainCustomer(record, {}, {});
  }
}

export async function decryptCustomerRecordList(
  encryption: EncryptionService,
  records: CustomerWithEncryptedPii[],
  onBatch?: (batch: Customer[]) => void,
): Promise<Customer[]> {
  return decryptInBatches(records, r => decryptCustomerRecord(encryption, r), onBatch);
}

function toPlainCustomer(
  record: CustomerWithEncryptedPii,
  plain: Record<string, string>,
  addrPlains: Record<number, Record<string, string>>,
): Customer {
  return {
    id:             record.id,
    name:           plain['name']           ?? record.name,
    primaryPhone:   plain['primaryPhone']   ?? record.primaryPhone,
    secondaryPhone: plain['secondaryPhone'] ?? record.secondaryPhone,
    email:          plain['email']          ?? record.email,
    isCompany:      record.isCompany,
    partnerId:      record.partnerId,
    partnerName:    record.partnerName,
    notes:          record.notes,
    addresses: record.addresses.map(a => ({
      id:          a.id,
      label:       a.label,
      address:     addrPlains[a.id]?.['address'] ?? a.address,
      areaId:      a.areaId,
      areaNameAr:  a.areaNameAr,
      areaNameEn:  a.areaNameEn,
      areaIsActive: a.areaIsActive,
      sortOrder:   a.sortOrder,
    })),
  };
}
